/**************************************************************************************************/
/*   File: /src/app/stores/reducers/creation/reducer.ts                                             */
/*   Project: newplan-frontend                                                                    */
/*   Created Date: Friday February 22nd 2019                                                      */
/*   Author: Andrea Zamboni andrea.zamboni@trilogis.it                                            */
/*   -----                                                                                        */
/*   Last Modified: Tue May 14 2019                                                               */
/*   Modified By: Andrea Zamboni                                                                  */
/*   -----                                                                                        */
/*   Copyright (c) 2019 Trilogis s.r.l. for SIAG                                                  */
/**************************************************************************************************/
import * as _ from 'lodash';

import * as fromShared from '../../../shared';
import * as fromActions from '../../actions';
import * as fromStates from '../../states';

/**
 * the dossier creation state reducer
 * @param {fromStates.fromCreation.CreationState} state The state
 * @param {fromActions.fromCreation.DossierCreationActions | fromActions.fromMessages.MessagesActions} action The action
 */
export function creationReducer(
  state: fromStates.fromCreation.CreationState = fromStates.fromCreation.INITIAL_DOSSIER_CREATION_STATE,
  action: fromActions.fromCreation.DossierCreationActions |
    fromActions.fromMessages.MessagesActions |
    any): fromStates.fromCreation.CreationState {

  switch (action.type) {
    case fromActions.fromCreation.DossierCreationActionsType.RESET_DOSSIER:
      return {
        ...fromStates.fromCreation.INITIAL_DOSSIER_CREATION_STATE
      };
    // Set is geometric change reducer
    case fromActions.fromCreation.DossierCreationActionsType.SET_IS_GEOMETRIC_CHANGE:
      return {
        ...state,
        dossierContent: {
          ...state.dossierContent,
          spatialDossier: action.payload.isGeometric
        }
      };
    // Set is norme change reducer
    case fromActions.fromCreation.DossierCreationActionsType.SET_IS_NORME_CHANGE:
      return {
        ...state,
        dossierContent: {
          ...state.dossierContent,
          regulationsDossier: action.payload.isNorme
        }
      };
    // Selected plan type reducer
    case fromActions.fromCreation.DossierCreationActionsType.SELECT_PLAN_TYPE:
      return {
        ...state,
        dossierContent: {
          ...state.dossierContent,
          planCode: action.payload.planCode,
          territorialAreasCodes: []
        }
      };
    // Add ambito reducer
    case fromActions.fromCreation.DossierCreationActionsType.ADD_AMBITO:
      let previousAmbiti = [];
      if (state.dossierContent !== undefined && state.dossierContent.territorialAreasCodes) {
        previousAmbiti = state.dossierContent.territorialAreasCodes;
      }
      return {
        ...state,
        dossierContent: {
          ...state.dossierContent,
          territorialAreasCodes: [
            ...previousAmbiti,
            action.payload.ambitoCode
          ]
        }
      };
    // Remove ambito reducer
    case fromActions.fromCreation.DossierCreationActionsType.REMOVE_AMBITO:
      return {
        ...state,
        dossierContent: {
          ...state.dossierContent,
          planCode: state.dossierContent.planCode,
          territorialAreasCodes: [
            ...state.dossierContent.territorialAreasCodes.filter(x => x !== action.payload.ambitoCode)
          ]
        }
      };
    // Selected geo file type reducer
    case fromActions.fromCreation.DossierCreationActionsType.SELECT_GEO_FILE_TYPE:
      return {
        ...state,
        dossierContent: {
          ...state.dossierContent,
          defaultSpatialDataType: action.payload.defaultSpatialDataType
        }
      };
    // Reducer for create dossier
    case fromActions.fromCreation.DossierCreationActionsType.CREATE_DOSSIER:
      return {
        ...state,
        isLoading: true
      };
    // Reducer for create dossier success
    case fromActions.fromCreation.DossierCreationActionsType.CREATE_DOSSIER_SUCCESS:
      return {
        ...fromStates.fromCreation.INITIAL_DOSSIER_CREATION_STATE,
        isLoading: false,
        dossierId: action.payload.dossierId
      };
    // Reducer for create dossier fail
    case fromActions.fromCreation.DossierCreationActionsType.CREATE_DOSSIER_FAIL:
      return {
        ...state,
        messages: [
          action.payload.message
        ]
      };
    // Reducer for load the ambiti
    case fromActions.fromCreation.DossierCreationActionsType.LOAD_AMBITI_BY_PLAN:
      return {
        ...state,
        isLoading: true,
        ambiti: [],
        messages: []
      };
    // Reducer for ambiti success
    case fromActions.fromCreation.DossierCreationActionsType.LOAD_AMBITI_BY_PLAN_SUCCESS:
      if (!action.payload.ambiti || action.payload.ambiti.length === 0) {
        return {
          ...state,
          isLoading: false,
          ambiti: [],
          messages: [
            new fromShared.models.Message(
              fromShared.enums.MessageType.ERROR,
              fromShared.enums.Messages.NO_AMBITI_FOR_PLAN,
              fromShared.enums.MessagePositionEnum.TOP,
              fromStates.fromUi.STATE_NAME
            )
          ]
        };
      }
      return {
        ...state,
        isLoading: false,
        ambiti: [
          ...action.payload.ambiti
        ]
      };
    // Reducer for ambiti fail
    case fromActions.fromCreation.DossierCreationActionsType.LOAD_AMBITI_BY_PLAN_FAIL:
      return {
        ...state,
        isLoading: false,
        ambiti: [],
        messages: [
          action.payload.message
        ]
      };
    case fromActions.fromUi.UiActionTypes.UNSET_LOGGED_USER:
      return {
        ...fromStates.fromCreation.INITIAL_DOSSIER_CREATION_STATE
      };
    default:
      return state;
  }
}

// Helpers
/**
 * Extract is loading from creation state
 * @param {fromStates.fromCreation.CreationState} state The state
 */
const getIsLoading = (state: fromStates.fromCreation.CreationState) => state.isLoading;
/**
 * Extract dossier id from creation state
 * @param {fromStates.fromCreation.CreationState} state The state
 */
const getDossierId = (state: fromStates.fromCreation.CreationState) => state.dossierId;
/**
 * Extract messages from creation state
 * @param {fromStates.fromCreation.CreationState} state The state
 */
const getMessages = (state: fromStates.fromCreation.CreationState) => state.messages;
/**
 * Extract dossier content from creation state
 * @param {fromStates.fromCreation.CreationState} state The state
 */
const getDossierContent = (state: fromStates.fromCreation.CreationState) => state.dossierContent;
/**
 * Get the ambiti
 * @param state The state
 */
const getAmbiti = (state: fromStates.fromCreation.CreationState) => state.ambiti;
/**
 * The helpers
 */
export const helpers = {
  getIsLoading,
  getDossierId,
  getMessages,
  getDossierContent,
  getAmbiti
};